// Recently-shared banner — soft notification that surfaces asset shares
// granted to the current user in the last 7 days. Dismissable per-user (stored
// in localStorage) so it doesn't nag after you've already seen it.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Images, X as XIcon, Share2, ChevronRight } from "lucide-react";

const DISMISS_KEY = "analyzio.recently_shared.dismissed";

function lastSeenDismissedAt() {
    try { return localStorage.getItem(DISMISS_KEY) || ""; }
    catch { return ""; }
}

export default function RecentlySharedWidget({ onEmpty }) {
    const [items, setItems] = useState(null);
    const [dismissedAt, setDismissedAt] = useState(lastSeenDismissedAt());
    const nav = useNavigate();

    const load = async () => {
        try {
            const r = await api.get("/gallery/widget/recently-shared?days=7");
            setItems(r.data?.items || []);
        } catch {
            setItems([]);
        }
    };

    useEffect(() => { load(); }, []);

    const visible = (items || []).filter((it) => !dismissedAt || it.granted_at > dismissedAt);
    const isEmpty = items !== null && visible.length === 0;
    useEffect(() => {
        if (typeof onEmpty === "function") onEmpty(isEmpty);
    }, [isEmpty, onEmpty]);

    const dismiss = () => {
        const latest = (items || []).reduce((mx, it) => it.granted_at > mx ? it.granted_at : mx, "");
        try { localStorage.setItem(DISMISS_KEY, latest || new Date().toISOString()); } catch {}
        setDismissedAt(latest);
    };

    if (items === null) {
        return <div className="border border-dashed border-border rounded-sm p-3 text-xs text-muted-foreground bg-card" data-testid="recently-shared-loading">Loading recent shares…</div>;
    }
    if (visible.length === 0) {
        // Hide entirely when empty — no "nothing to see here" noise on Home.
        return <div className="hidden" data-testid="recently-shared-empty" />;
    }

    return (
        <div className="border border-primary/30 bg-primary/5 rounded-sm p-3 h-full overflow-hidden" data-testid="home-widget-recently-shared">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <Share2 className="h-3.5 w-3.5 text-primary" />
                    <span className="eyebrow text-[10px]">Recently shared with you</span>
                    <span className="font-mono text-[10px] text-muted-foreground">· last 7 days</span>
                </div>
                <button
                    onClick={dismiss}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="recently-shared-dismiss"
                    title="Dismiss until next share">
                    <XIcon className="h-3.5 w-3.5" />
                </button>
            </div>
            <ul className="space-y-1 max-h-[180px] overflow-y-auto scrollbar-thin pr-1">
                {visible.slice(0, 6).map((it) => (
                    <li key={it.share_id}>
                        <button
                            onClick={() => nav(it.deeplink)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-background border border-transparent hover:border-border transition-all text-left group"
                            data-testid={`recently-shared-item-${it.share_id}`}>
                            <Images className="h-3 w-3 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs truncate">
                                    <span className="font-medium">{it.asset_name}</span>
                                    <span className="text-muted-foreground">
                                        {" "}· {it.granted_by_name} from {it.source_org_name}
                                    </span>
                                </div>
                            </div>
                            <span className="font-mono text-[9px] px-1.5 py-0.5 border border-border rounded-sm uppercase text-muted-foreground">
                                {it.access}
                            </span>
                            <ChevronRight className="h-3 w-3 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
